<script setup lang="ts">
import { computed } from 'vue';

import IconCheckCircle from '~/assets/icons/IconCheckCircle.vue';
import IconFileText from '~/assets/icons/IconFileText.vue';
import StatusCard from '~/components/shared/StatusCard.vue';
import { FinancingFlowApi } from '~/services/financing-flow-api/financing-flow.types';

interface BiometryContractSection {
  completedAt: { value: null | string | undefined };
  lastSubmittedAt: { value: string };
  submittedAt: { value: null | string | undefined };
  text: { value: string };
  type: { value: string };
}

interface Props {
  biometrySection?: BiometryContractSection;
  contractSection?: BiometryContractSection;
  financing: FinancingFlowApi.FinancingV2;
}

const props = defineProps<Props>();

const completedAt = computed(
  () => props.financing.section_statuses.biometry_and_contract?.completed_at || undefined,
);
const submittedAt = computed(
  () => props.financing.section_statuses.biometry_and_contract?.submitted_at || undefined,
);

const getBiometrySubmittedDate = computed(() => {
  const date = props.financing.section_statuses?.biometry_and_contract?.metadata?.biometry?.last_submitted_at;
  return date || undefined;
});

const getBiometryCompletedDate = computed(() => {
  const date = props.financing.section_statuses?.biometry_and_contract?.metadata?.biometry?.completed_at;
  return date || undefined;
});

const getContractSubmittedDate = computed(() => {
  const date = props.financing.section_statuses?.biometry_and_contract?.metadata?.contract?.last_submitted_at;
  return date || undefined;
});

const getContractCompletedDate = computed(() => {
  const date = props.financing.section_statuses?.biometry_and_contract?.metadata?.contract?.completed_at;
  return date || undefined;
});
</script>

<template>
  <div class="biometry-contract-step">
    <div class="mb-4">
      <div class="row g-3">
        <div class="status-card-col">
          <StatusCard
            title="Submetido em"
            :datetime="submittedAt"
            :icon="IconFileText"
            type="informative"
          />
        </div>

        <div class="status-card-col">
          <StatusCard
            title="Concluído em"
            :datetime="completedAt"
            :icon="IconCheckCircle"
            type="positive"
          />
        </div>
      </div>
    </div>

    <div class="subsection mb-4">
      <div class="subsection-header">
        <h5 class="subsection-title">
          <i class="ti ti-scan" />
          Biometria
        </h5>
        <span class="status-badge" :class="biometrySection?.type?.value || 'neutral'">
          {{ biometrySection?.text?.value || 'Aguardando' }}
        </span>
      </div>
      <div class="row g-3 mt-2">
        <div class="status-card-col">
          <StatusCard
            title="Último Envio"
            :datetime="getBiometrySubmittedDate"
            :icon="IconFileText"
            :type="getBiometrySubmittedDate ? 'informative' : 'default'"
          />
        </div>
        <div class="status-card-col">
          <StatusCard
            title="Concluído em"
            :datetime="getBiometryCompletedDate"
            :icon="IconCheckCircle"
            :type="getBiometryCompletedDate ? 'positive' : 'default'"
          />
        </div>
      </div>
    </div>

    <div class="subsection">
      <div class="subsection-header">
        <h5 class="subsection-title">
          <i class="ti ti-file-text" />
          Contrato
        </h5>
        <span class="status-badge" :class="contractSection?.type?.value || 'neutral'">
          {{ contractSection?.text?.value || 'Aguardando' }}
        </span>
      </div>
      <div class="row g-3 mt-2">
        <div class="status-card-col">
          <StatusCard
            title="Último Envio"
            :datetime="getContractSubmittedDate"
            :icon="IconFileText"
            :type="getContractSubmittedDate ? 'informative' : 'default'"
          />
        </div>
        <div class="status-card-col">
          <StatusCard
            title="Concluído em"
            :datetime="getContractCompletedDate"
            :icon="IconCheckCircle"
            :type="getContractCompletedDate ? 'positive' : 'default'"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.biometry-contract-step {
  .status-card-col {
    @media (min-width: 768px) {
      flex: 0 0 auto;
      width: 50%;
    }
  }

  .subsection {
    background: #f8f9fa;
    border-radius: 0.75rem;
    padding: 1.25rem;
  }

  .subsection-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.5rem;
  }

  .subsection-title {
    font-size: 1.125rem;
    font-weight: 700;
    margin: 0;
    color: #1e293b;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    i {
      font-size: 1.25rem;
    }
  }

  .status-badge {
    font-size: 0.75rem;
    font-weight: 600;
    padding: 0.25rem 0.625rem;
    border-radius: 0.375rem;
    display: inline-flex;
    align-items: center;
    white-space: nowrap;

    &.informative { background: #e3f2fd; color: #1976d2; }
    &.negative { background: #fdecea; color: #d32f2f; }
    &.neutral { background: #f4f6f8; color: #616161; }
    &.positive { background: #e8f5e9; color: #388e3c; }
    &.warning { background: #fff8e1; color: #b26a00; }
  }
}
</style>
